import { useEffect, useState } from "react";
import { get, patchRequest } from "../../../http/api";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";


const Skeleton = ({ height = 42 }) => (
  <div className="skeleton" style={{ height }} />
);

export default function BasicInfo() {

  const [form, setForm] = useState({
    username: "",
    language: "fr",
    bio: "",
    link1: "",
    link2: ""
  });

  const [linkError, setLinkError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [usernameChangesRemaining, setUsernameChangesRemaining] = useState(2);
  const [savedUsername, setSavedUsername] = useState("");

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [success, setSuccess] = useState(false);

  const { t } = useTranslation();



  useEffect(() => {
    fetchBasicInfo();
  }, []);

  const fetchBasicInfo = async () => {
    setLoadingData(true);
    try {
      const res = await get("profile/basic-info");
      setUsernameChangesRemaining(Number(res.data.username_changes_remaining ?? 2));
      setSavedUsername(String(res.data.username || "").toLowerCase());
      setForm({
        username: res.data.username || "",
        language: res.data.language || "fr",
        country: res.data.country || "",
        city: res.data.city || "",
        bio: res.data.bio || "",
        link1: res.data.link1 || "",
        link2: res.data.link2 || ""
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  };


  const handleChange = (e) => {

    const { name, value } = e.target;
    const safeValue = name === "username"
      ? value.replace(/[^a-zA-Z0-9._]/g, "").slice(0, 30)
      : value;

    setForm({
      ...form,
      [name]: safeValue
    });

    if (name === "language") {

      i18n.changeLanguage(value);

      localStorage.setItem(
        "siteLanguage",
        value
      );
    }

    setSuccess(false);
    if (name === "username") {
      setUsernameError(
        value === safeValue ? "" : t("registerErrors.invalidUsername")
      );
    }
  };


  const isValidUrl = (url) => {

    if (!url) return true;

    const regex =
      /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w\-./?%&=]*)?$/;
    return regex.test(url);
  };

  const handleSave = async () => {

    setLinkError("");
    const normalizedUsername = form.username.trim().toLowerCase();
    if (!/^[a-zA-Z0-9._]{3,30}$/.test(normalizedUsername)) {
      setUsernameError(t("registerErrors.invalidUsername"));
      return;
    }

    if (
      !isValidUrl(form.link1)
    ) {

      setLinkError(
        t("basicInfo.errors.invalidLink")
      );

      return;
    }

    if (
      !isValidUrl(form.link2)
    ) {

      setLinkError(
        "Veuillez entrer un lien valide"
      );

      return;
    }

    setLoading(true);

    try {

      const usernameChanged = normalizedUsername !== savedUsername;

      const result = await patchRequest(
        "profile/basic-info",
        { ...form, username: normalizedUsername }
      );

      const nextUsername = result?.data?.username || normalizedUsername;
      setUsernameChangesRemaining(
        Number(
          result?.data?.username_changes_remaining ??
          (usernameChanged
            ? Math.max(0, usernameChangesRemaining - 1)
            : usernameChangesRemaining)
        )
      );
      setForm(prev => ({ ...prev, username: nextUsername }));
      setSavedUsername(nextUsername);
      const storedUser = JSON.parse(sessionStorage.getItem("user") || "null");
      if (storedUser) {
        const nextUser = { ...storedUser, username: nextUsername };
        sessionStorage.setItem("user", JSON.stringify(nextUser));
        try {
          const remembered = JSON.parse(
            localStorage.getItem("carrerup_remembered_auth") || "null"
          );
          if (remembered?.user) {
            localStorage.setItem(
              "carrerup_remembered_auth",
              JSON.stringify({ ...remembered, user: nextUser })
            );
          }
        } catch (_) {}
        window.dispatchEvent(
          new CustomEvent("carrerup:username-changed", {
            detail: { username: nextUsername }
          })
        );
      }
      setUsernameError("");

      setSuccess(true);

    }
    catch (err) {

      const message = err?.message === "USERNAME_TAKEN"
        ? t("registerErrors.usernameUsed")
        : err?.message === "USERNAME_CHANGE_LIMIT"
          ? t("basicInfo.usernameChangeLimit")
          : t("registerErrors.invalidUsername");
      setUsernameError(message);

    }
    finally {

      setLoading(false);

    }

  };

  return (
    <section className="settings-card">
      <h3>{t("basicInfo.title")}</h3>

      <label>{t("basicInfo.fields.publicName")}</label>
      {loadingData ? (
        <Skeleton />
      ) : (
        <input
          name="username"
          value={form.username}
          onChange={handleChange}
          maxLength={30}
          autoCapitalize="none"
          placeholder={t("basicInfo.placeholders.displayName")}
        />

      )}
      {usernameError && <p className="settings-field-error">{usernameError}</p>}
      <p className="settings-username-rule">
        {t("basicInfo.usernameChangeRule", { remaining: usernameChangesRemaining })}
      </p>


      <label>
        {t("basicInfo.fields.language")}
      </label>

      {loadingData ? (
        <Skeleton />
      ) : (
        <select
          name="language"
          value={form.language}
          onChange={handleChange}
          className="settings-select"
        >
          <option value="fr">
            Français 🇫🇷
          </option>

          <option value="en">
            English 🇬🇧
          </option>

          <option value="ar">
            العربية 🇲🇦
          </option>

          <option value="es">
            Español 🇪🇸
          </option>
          <option value="pt">Português 🇵🇹</option>
          <option value="zh">中文（简体）🇨🇳</option>
          <option value="de">Deutsch 🇩🇪</option>
          <option value="ru">Русский 🇷🇺</option>
          <option value="it">Italiano 🇮🇹</option>
          <option value="sr">Srpski 🇷🇸</option>
        </select>
      )}



      <label>{t("basicInfo.fields.bio")}</label>
      {loadingData ? (
        <Skeleton height={80} />
      ) : (
        <textarea
          name="bio"
          maxLength={160}
          value={form.bio}
          onChange={handleChange}
          placeholder={t("basicInfo.placeholders.bio")}
        />
      )}

      <label>{t("basicInfo.fields.links")}</label>

      <div className="links-wrapper">

        <input
          name="link1"
          value={form.link1}
          onChange={handleChange}
          placeholder={t("basicInfo.placeholders.firstLink")}
        />

        {!form.link2 && form.link1 && (

          <button
            type="button"
            className="add-link-btn"
            onClick={() =>
              setForm(prev => ({

                ...prev,
                link2: " "

              }))
            }
          >

            +

          </button>

        )}

      </div>

      {form.link2 !== "" && (

        <div
          className="links-wrapper"
        >

          <input
            name="link2"
            value={form.link2}
            onChange={handleChange}
            placeholder={t("basicInfo.placeholders.secondLink")}
          />

        </div>

      )}

      {linkError && (

        <div className="error-message">

          ✖ {linkError}

        </div>

      )}

      <div className="settings-actions">
        <button
          className="btn primary"
          onClick={handleSave}
          disabled={loading}
        >
          {
            loading
              ? t("basicInfo.buttons.saving")
              : t("basicInfo.buttons.save")
          }
        </button>

        {success && <span className="success-msg">{t("basicInfo.success.saved")}</span>}
      </div>
    </section>
  );
}
